import React from 'react';
import type { ComposeAction } from './composeActions';

export interface ChipRowBench {
  id: string;
  label: string;
  actions: ComposeAction[];
}

export interface ChipRowLabProps {
  benches: ChipRowBench[];
  activeBenchId: string;
  onBenchClick: (benchId: string) => void;
}

export interface ChipRowEmptyState {
  message: string;
  ctaLabel: string;
  onCta: () => void;
}

export interface ChipRowProps {
  /** Top-level pills to render (non-Lab tab's actions, or active bench's actions when in Lab). */
  actions: ComposeAction[];
  /** When present, renders Lab 2-row variant (bench strip + items strip). Single discriminated prop. */
  lab?: ChipRowLabProps;
  /** Action click handler. Parent dispatches; no retract — workshop stays open. */
  onActionClick: (action: ComposeAction) => void;
  /** Conversation loading state — disables pills to prevent racy double-fires. */
  loading: boolean;
  /** Optional CTA shown when actions=[] — caller provides message + ctaLabel + onCta. */
  emptyState?: ChipRowEmptyState;
}

const PROMPT_PREVIEW_MAX = 160;
const promptPreview = (a: ComposeAction): string => {
  if (a.kind === 'navigate') return `Navigate: ${a.route}`;
  // v0.1 Item 8 — 'exec' actions have no prompt; preview falls back to
  // the descriptor (which the bench authors fill out per-action).
  if (a.kind === 'exec') return a.descriptor ?? a.label;
  return a.prompt.length > PROMPT_PREVIEW_MAX
    ? `${a.prompt.slice(0, PROMPT_PREVIEW_MAX)}…`
    : a.prompt;
};

/**
 * Chip-pill row beneath the active JourneyTabs tab. When `lab` is
 * provided, renders a 2-row layout (bench strip + items strip).
 *
 * v0.5+: dropped framer-motion entirely. The workshop is always-open
 * now (per operator field-test), so the chip-row is always mounted;
 * tab swaps are pure React content re-renders. Snappy and instant.
 *
 * Active bench encoded by bg-amber pill (no • marker).
 *
 * Tailwind `dark:` variants throughout per `feedback_dark_mode_parity_first_class`.
 * Pure presentational. Parent owns activeTabId / activeBenchId state.
 */
export const ChipRow: React.FC<ChipRowProps> = ({
  actions,
  lab,
  onActionClick,
  loading,
  emptyState,
}) => {
  return (
    <div
      data-testid="chip-row"
      className="overflow-hidden border-b border-amber-200 dark:border-gray-700 bg-white/40 dark:bg-gray-900/30"
    >
      {lab && (
        <div
          data-testid="chip-row-bench-strip"
          className="flex flex-wrap items-center gap-1 px-3 py-1.5 border-b border-amber-100 dark:border-gray-700 bg-amber-50/50 dark:bg-gray-900/40"
        >
          {lab.benches.map((bench) => {
            const active = bench.id === lab.activeBenchId;
            return (
              <button
                key={bench.id}
                type="button"
                data-testid={`chip-row-bench-${bench.id}`}
                aria-pressed={active}
                onClick={() => lab.onBenchClick(bench.id)}
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium transition-colors ${
                  active
                    ? 'bg-amber-200/70 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200'
                    : 'text-gray-700 hover:bg-amber-100/60 dark:text-gray-300 dark:hover:bg-gray-700/40'
                }`}
              >
                {bench.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="px-3 py-2">
        {actions.length === 0 && emptyState ? (
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>{emptyState.message}</span>
            <button
              type="button"
              onClick={emptyState.onCta}
              className="underline text-amber-700 dark:text-amber-400"
            >
              {emptyState.ctaLabel}
            </button>
          </div>
        ) : actions.length === 0 ? (
          <div className="text-xs italic text-gray-400 dark:text-gray-500">
            No actions in this tab.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                data-testid={`chip-pill-${action.id}`}
                disabled={loading}
                title={promptPreview(action)}
                onClick={() => onActionClick(action)}
                className={`text-[12px] leading-tight px-2.5 py-1 rounded-full whitespace-nowrap transition-colors border bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-transparent dark:text-gray-200 dark:hover:bg-gray-600 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {action.emoji && <span aria-hidden className="mr-1">{action.emoji}</span>}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
